// Completion.js
import React from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { ASSESSMENT_TYPES } from 'modules/Assessments/actions'

// Container for the entire card
const Container = styled.div`
  width: 95%;
  max-width: 1400px;
  min-height: 500px;
  margin: 80px auto;
  padding: 30px;
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
  display: flex;
  flex-direction: column;
  font-family: 'Plus Jakarta Sans', sans-serif; /* Ensures consistent font */
`

// Header section for Title and Timer
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`

const Title = styled.h1`
  font-size: 20px;
  font-weight: 500;
  color: #333333;
  margin: 0;
`

// Timer with red outline/background
const AssessmentTimer = styled.div`
  padding: 12px 24px;
  background: #fff1f1;
  border-radius: 8px;
  color: #e53e3e;
  font-size: 16px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border: 1px solid #e53e3e;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

// Subheader for Assessment Name
const AssessmentName = styled.div`
  color: #666666;
  font-size: 16px;
  margin-bottom: 40px;
`

const ThankYouText = styled.h2`
  font-size: 24px;
  font-weight: 500;
  color: #333333;
  margin-bottom: 20px;
`

const Message = styled.p`
  font-size: 16px;
  color: #666666;
  line-height: 1.6;
  margin: 0;
  flex-grow: 1;
`

// Next button with updated size, spacing, and hover effects
const NextButton = styled.button`
  position: relative;
  margin-top: 40px;
  padding: 14px 36px;
  background: #4640de;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.2s ease;
  align-self: flex-start; /* or flex-end / center, depending on desired alignment */

  &:hover {
    background: #3530ca;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`

const Completion = ({ assessmentData, submittedTime, setAssessment, isPractice = false }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Formats time from seconds -> mm:ss
  const formatTime = seconds => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`
  }

  const handleNext = () => {
    // Exit fullscreen if we're still in it
    if (document.fullscreenElement) {
      try {
        document.exitFullscreen().catch(err => 
          console.error('Error exiting fullscreen on completion:', err)
        );
      } catch (error) {
        console.error('Error in exitFullscreen on completion:', error);
      }
    }

    // Only dispatch the success action if it's not a practice assessment
    if (!isPractice) {
      dispatch({
        type: ASSESSMENT_TYPES.SUBMIT_ASSESSMENT_SUCCESS,
      });
    } else {
      // For practice assessments, navigate back to placementprep
      localStorage.removeItem('isPracticeAssessment');
      navigate('/placementprep');
      return;
    }
    
    // For regular assessments, clear the assessment state
    setAssessment(null);
  }

  return (
    <Container>
      <Header>
        <Title>{assessmentData?.title || 'Communication Assessment'}</Title>
        <AssessmentTimer>Timer : {formatTime(submittedTime)}</AssessmentTimer>
      </Header>
      <AssessmentName>
        Assessment Name : {assessmentData?.type || 'Communication Assessment'}
        {isPractice && " (Practice)"}
      </AssessmentName>
      <ThankYouText>
        {isPractice 
          ? "Thank you for completing the practice assessment!" 
          : "Thank you for attending the assessment!"}
      </ThankYouText>
      <Message>
        {isPractice
          ? "This was a practice assessment to help you prepare. You can take more practice assessments to improve your skills."
          : "Your result is being processed and will be available shortly. Please check the \"Completed Assessment\" tab to view your score."}
      </Message>
      <NextButton onClick={handleNext}>
        {isPractice ? "Return to Assessments" : "Next"}
      </NextButton>
    </Container>
  )
}

export default Completion
